import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExpiringContracts } from '../../../Redux/Actions/actions';
import { FaCalendarAlt, FaExclamationTriangle, FaHome, FaUser } from 'react-icons/fa';
import style from './ExpiringContracts.module.css';

const ExpiringContracts = () => {
  const dispatch = useDispatch();
  const { expiringContracts, rentalsLoading, rentalsError } = useSelector(state => state);
  const [daysFilter, setDaysFilter] = useState(90);

  useEffect(() => {
    dispatch(fetchExpiringContracts(daysFilter));
  }, [dispatch, daysFilter]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const getDaysToExpiry = (endDate) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyLevel = (days) => {
    if (days <= 30) return 'critical';
    if (days <= 60) return 'warning';
    return 'info';
  };

  const getUrgencyText = (days) => {
    if (days <= 0) return 'Vencido';
    if (days <= 30) return 'Crítico';
    if (days <= 60) return 'Urgente';
    return 'Próximo a vencer';
  };

  if (rentalsLoading) {
    return <div className={style.loading}>Cargando contratos por vencer...</div>;
  }

  if (rentalsError) {
    return <div className={style.error}>Error: {rentalsError}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaCalendarAlt className={style.titleIcon} />
          Contratos por Vencer
        </h1>
        
        <div className={style.filters}>
          <label className={style.filterLabel}>Mostrar contratos que vencen en:</label>
          <select 
            value={daysFilter} 
            onChange={(e) => setDaysFilter(Number(e.target.value))}
            className={style.filterSelect}
          >
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
            <option value={180}>6 meses</option>
          </select>
        </div>
      </div>

      <div className={style.summary}>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Total Contratos</span>
          <span className={style.summaryValue}>{expiringContracts.length}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Críticos (≤30 días)</span>
          <span className={`${style.summaryValue} ${style.critical}`}>
            {expiringContracts.filter(c => getDaysToExpiry(c.contractEnd) <= 30).length}
          </span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Urgentes (≤60 días)</span>
          <span className={`${style.summaryValue} ${style.warning}`}>
            {expiringContracts.filter(c => getDaysToExpiry(c.contractEnd) <= 60).length}
          </span>
        </div>
      </div>

      {expiringContracts.length === 0 ? (
        <div className={style.noData}>
          <FaCalendarAlt className={style.noDataIcon} />
          <h3>No hay contratos próximos a vencer</h3>
          <p>En los próximos {daysFilter} días</p>
        </div>
      ) : (
        <div className={style.contractsList}>
          {expiringContracts
            .sort((a, b) => getDaysToExpiry(a.contractEnd) - getDaysToExpiry(b.contractEnd))
            .map((contract) => {
              const daysToExpiry = getDaysToExpiry(contract.contractEnd);
              const urgencyLevel = getUrgencyLevel(daysToExpiry);
              
              return (
                <div 
                  key={contract.id} 
                  className={`${style.contractCard} ${style[urgencyLevel]}`}
                >
                  <div className={style.cardHeader}>
                    <div className={style.propertyInfo}>
                      <FaHome className={style.propertyIcon} />
                      <div>
                        <h3 className={style.propertyTitle}>
                          {contract.property?.title || 'Propiedad sin título'}
                        </h3>
                        <p className={style.propertyAddress}>
                          {contract.property?.street} {contract.property?.number}, {contract.property?.locality}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`${style.urgencyBadge} ${style[urgencyLevel]}`}>
                      <FaExclamationTriangle />
                      {getUrgencyText(daysToExpiry)}
                    </div>
                  </div>

                  <div className={style.cardContent}>
                    <div className={style.contractInfo}>
                      <div className={style.infoItem}>
                        <FaUser className={style.infoIcon} />
                        <div>
                          <span className={style.infoLabel}>Inquilino Principal</span>
                          <span className={style.infoValue}>
                            {contract.tenants?.find(t => t.tenantType === 'main')?.fullName || 'No asignado'}
                          </span>
                        </div>
                      </div>

                      <div className={style.infoItem}>
                        <FaCalendarAlt className={style.infoIcon} />
                        <div>
                          <span className={style.infoLabel}>Fecha de Vencimiento</span>
                          <span className={style.infoValue}>
                            {formatDate(contract.contractEnd)}
                          </span>
                          <span className={`${style.daysLeft} ${style[urgencyLevel]}`}>
                            {daysToExpiry > 0 ? `${daysToExpiry} días restantes` : 'Vencido'}
                          </span>
                        </div>
                      </div>

                      <div className={style.infoItem}>
                        <span className={style.infoLabel}>Duración del Contrato</span>
                        <span className={style.infoValue}>
                          {formatDate(contract.contractStart)} - {formatDate(contract.contractEnd)}
                        </span>
                      </div>

                      <div className={style.infoItem}>
                        <span className={style.infoLabel}>Alquiler Actual</span>
                        <span className={style.infoValue}>
                          {new Intl.NumberFormat('es-AR', {
                            style: 'currency',
                            currency: 'ARS',
                            minimumFractionDigits: 0
                          }).format(contract.currentRentValue || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={style.cardActions}>
                    <button 
                      className={`${style.actionButton} ${style.primary}`}
                      onClick={() => console.log('Renovar contrato:', contract.id)}
                    >
                      Iniciar Renovación
                    </button>
                    <button 
                      className={`${style.actionButton} ${style.secondary}`}
                      onClick={() => console.log('Contactar inquilino:', contract.id)}
                    >
                      Contactar Inquilino
                    </button>
                    <button 
                      className={`${style.actionButton} ${style.secondary}`}
                      onClick={() => console.log('Ver contrato:', contract.id)}
                    >
                      Ver Detalles
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ExpiringContracts;